import React from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  InputBase,
  IconButton,
  Chip,
  Divider,
  Card,
  CardHeader,
  CardContent,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";

const VendorInventory = () => {
  // Sample data for inventory items
  const inventoryItems = [
    {
      id: "0001",
      category: "Category 5",
      inStock: 46,
      minimumLevel: 71,
      status: "In Stock"
    },
    {
      id: "0002",
      category: "Category 3",
      inStock: 997,
      minimumLevel: 12,
      status: "In Stock"
    },
    {
      id: "0003",
      category: "Category 1",
      inStock: 607,
      minimumLevel: 2,
      status: "In Stock"
    },
    {
      id: "0004",
      category: "Category 3",
      inStock: 94,
      minimumLevel: 1,
      status: "In Stock"
    },
    {
      id: "0005",
      category: "Category 5",
      inStock: 549,
      minimumLevel: 26,
      status: "In Stock"
    },
    {
      id: "0006",
      category: "Category 4",
      inStock: 73,
      minimumLevel: 88,
      status: "In Stock"
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case "In Stock":
        return { bg: "#DCFCE7", color: "#10B981" };
      case "Low Stock":
        return { bg: "#FEF3C7", color: "#F59E0B" };
      case "Out of Stock":
        return { bg: "#FEE2E2", color: "#EF4444" };
      default:
        return { bg: "#F3F4F6", color: "#6B7280" };
    }
  };

  return (
    <div className="page-container">
      <Card
        className="shadow-sm"
        variant="outlined"
        sx={{
          border: "1px solid lightgray",
          borderRadius: "10px",
          width: "100%",
        }}
      >
        <CardHeader
          title={<Typography variant="h5" sx={{ fontWeight: 400, fontSize: '24px', color: '#333' }} className="font-rubik">Inventory</Typography>}
          action={
            <div>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                sx={{
                  marginRight: "8px",
                  backgroundColor: "#050092",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#050092" },
                  fontWeight: 400,
                }}
                className="font-rubik"
              >
                New Order
              </Button>
            </div>
          }
          sx={{
            margin: "15px",
            display: "flex",
            justifyContent: "space-between",
          }}
        />
        <Divider sx={{ backgroundColor: "darkgray" }} />

        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}
            >
              <Paper
                component="form"
                sx={{
                  p: "2px 4px",
                  display: "flex",
                  alignItems: "center",
                  width: 250,
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  boxShadow: "none",
                }}
              >
                <InputBase
                  sx={{ ml: 1, flex: 1 }}
                  placeholder="Search inventory..."
                  inputProps={{ "aria-label": "search inventory" }}
                />
                <IconButton
                  type="submit"
                  sx={{ p: "10px" }}
                  aria-label="search"
                >
                  <SearchIcon />
                </IconButton>
              </Paper>

              <Button
                variant="outlined"
                startIcon={<FilterListIcon />}
                sx={{
                  borderColor: "#e0e0e0",
                  color: "#6B7280",
                  textTransform: "none",
                  borderRadius: "8px",
                }}
              >
                Filter
              </Button>
            </Box>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              sx={{
                bgcolor: "#050092",
                "&:hover": { bgcolor: "#050092" },
                borderRadius: "8px",
                textTransform: "none",
                display: { xs: "none", sm: "flex" },
                fontWeight: 400,
              }}
              className="font-rubik"
            >
              Add Item
            </Button>
          </Box>

          <Grid container spacing={3}>
            {inventoryItems.map((item) => {
              const statusStyle = getStatusColor(item.status);
              return (
                <Grid item xs={12} sm={6} md={4} key={item.id}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: "8px",
                      height: "100%",
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
                      border: "1px solid #f0f0f0",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: "medium", color: "#111827", fontFamily: 'Rubik' }}
                      >
                        Part #{item.id}
                      </Typography>
                      <IconButton
                        size="small"
                        sx={{ color: "#6B7280", padding: "2px" }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: "#6B7280", mb: 2, fontFamily: 'Rubik' }}
                    >
                      {item.category}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#6B7280", fontFamily: 'Rubik' }}>
                        In Stock
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "medium", color: "#111827", fontFamily: 'Rubik' }}
                      >
                        {item.inStock}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#6B7280", fontFamily: 'Rubik' }}>
                        Minimum Level
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: "medium", color: "#111827", fontFamily: 'Rubik' }}
                      >
                        {item.minimumLevel}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mt: "auto",
                        pt: 2,
                      }}
                    >
                      <Typography variant="body2" sx={{ mr: 2, fontFamily: 'Rubik' }}>
                        Status
                      </Typography>
                      <Chip
                        label={item.status}
                        sx={{
                          bgcolor: statusStyle.bg,
                          color: statusStyle.color,
                          fontWeight: "medium",
                          borderRadius: "16px",
                          fontSize: "0.75rem",
                          height: "24px",
                        }}
                      />
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>
    </div>
  );
};

export default VendorInventory;
